import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDropdown, { DropdownOption } from './custom-dropdown.component';
import { SearchInput } from './search/search-input.components';

interface DeductionFiltersProps {
    selectedYear: string;
    selectedMonth: string;
    selectedClient: string;
    searchQuery: string;
    startDate?: string;
    endDate?: string;
    onYearChange: (value: string) => void;
    onMonthChange: (value: string) => void;
    onClientChange: (value: string) => void;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStartDateChange?: (date: string) => void;
    onEndDateChange?: (date: string) => void;
    clients?: DropdownOption[];
    loading?: boolean;
}

const DeductionFilters: React.FC<DeductionFiltersProps> = ({
    selectedYear,
    selectedMonth,
    selectedClient,
    searchQuery,
    startDate = '',
    endDate = '',
    onYearChange,
    onMonthChange,
    onClientChange,
    onSearchChange,
    onStartDateChange,
    onEndDateChange,
    clients = [],
    loading = false
}) => {
    const { t } = useTranslation();
    const [dateError, setDateError] = useState<string>('');

    // Re-validate when dates change (e.g. parent reset)
    useEffect(() => {
        if (startDate && endDate && startDate > endDate) {
            setDateError(t('START_DATE_AFTER_END_DATE') || 'Start date cannot be after end date');
        } else {
            setDateError('');
        }
    }, [startDate, endDate]);

    const handleStartDateChange = (date: string) => {
        if (date && endDate && date > endDate) {
            setDateError(t('START_DATE_AFTER_END_DATE') || 'Start date cannot be after end date');
        } else {
            setDateError('');
        }
        onStartDateChange?.(date);
    };

    const handleEndDateChange = (date: string) => {
        if (date && startDate && date < startDate) {
            setDateError(t('END_DATE_BEFORE_START_DATE') || 'End date cannot be before start date');
        } else {
            setDateError('');
        }
        onEndDateChange?.(date);
    };

    // Generate year options (from 1980 to current year)
    const currentYear = new Date().getFullYear();
    const yearCount = currentYear - 1980 + 1;
    const yearOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_YEARS') || 'All Years' },
        ...Array.from({ length: yearCount }, (_, i) => ({
            value: String(currentYear - i),
            label: String(currentYear - i)
        }))
    ];
        const invoicingOptions: DropdownOption[] = [
        { value: 'invoiced', label: t('INVOICED_LABEL')},
        { value: 'non-invoiced', label: t('NON_INVOICED_LABEL')},

    ];

    // Month options
    const monthOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_MONTHS') || 'All Months' },
        { value: '1', label: t('JANUARY') || 'January' },
        { value: '2', label: t('FEBRUARY') || 'February' },
        { value: '3', label: t('MARCH') || 'March' },
        { value: '4', label: t('APRIL') || 'April' },
        { value: '5', label: t('MAY') || 'May' },
        { value: '6', label: t('JUNE') || 'June' },
        { value: '7', label: t('JULY') || 'July' },
        { value: '8', label: t('AUGUST') || 'August' },
        { value: '9', label: t('SEPTEMBER') || 'September' },
        { value: '10', label: t('OCTOBER') || 'October' },
        { value: '11', label: t('NOVEMBER') || 'November' },
        { value: '12', label: t('DECEMBER') || 'December' }
    ];

    // Client options from API with "All Customers" option
    const clientOptions: DropdownOption[] = [
        { value: 'all', label: t('ALL_CUSTOMERS') || 'All Customers' },
        ...clients
    ];

    return (
        <div className="deduction-filters">
            <div className="deduction-filters__row">
                     <div className="deduction-filters__search">
                    <SearchInput
                        value={searchQuery}
                        onChange={onSearchChange}
                        placeholder={t('SEARCH_PLACEHOLDER') || 'Search here...'}
                        className="deduction-search"
                    />
                </div>
                
                 <div className="deduction-filters__dropdown deduction-filters__dropdown--pill mr-0" style={{ flexShrink: 0 }}>
                    <CustomDropdown
                        options={invoicingOptions}
                        value={selectedYear}
                        onSelect={onYearChange}
                        placeholder={t('INVOCING_STATUS')}
                        className="deduction-year-dropdown mr-0"
                        fullWidth={false}
                        triggerClassName=" w-auto mx-0 shadow-none"
                        style={{ outline: 'none' }}
                        renderTrigger={(selectedOption, isOpen) => (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 12px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none', boxShadow: 'none' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '8px', background: '#d1d5db', flexShrink: 0  }} />
                                <span style={{ color: '#1C1B1F', fontSize: '14px', fontWeight: 500 }}>
                                    {selectedOption ? selectedOption.label : t('INVOCING_STATUS')}
                                </span>
                                <svg style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} width="12" height="8" viewBox="0 0 12 8" fill="none">
                                    <path d="M1 1.5L6 6.5L11 1.5" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                        )}
                        renderOption={(option, isSelected) => (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isSelected ? '#6750A8' : '#C4C4C4', flexShrink: 0 }} />
                                <span style={{ flex: 1, color: '#1C1B1F', fontSize: '14px' }}>{option.label}</span>
                                {isSelected && (
                                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                                        <path d="M1 5L5 9L13 1" stroke="#6750A8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </span>
                        )}
                    />
                </div>
                <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={clientOptions}
                        value={selectedClient}
                        onSelect={onClientChange}
                        placeholder={t('SELECT_CUSTOMER') || 'Select Customer'}
                        className="deduction-client-dropdown"
                        disabled={loading}
                    />
                </div>


                {/* Date Range In One Thread */}
                {onStartDateChange && onEndDateChange && (
                    <div className="deduction-filters__date-input">
                        <div className="date-range-thread">
                            <div className="date-inline-field">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                    onKeyDown={(e) => { if (e.key !== 'Tab') e.preventDefault(); }}
                                    className={`date-input-field date-input-field--range date-input-field--start${dateError ? ' date-input-field--error' : ''}`}
                                    disabled={loading}
                                />
                                <span className="date-inline-field__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                    </svg>
                                </span>
                            </div>
                            <span className="date-range-thread__separator">-</span>
                            <div className="date-inline-field">
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleEndDateChange(e.target.value)}
                                    onKeyDown={(e) => { if (e.key !== 'Tab') e.preventDefault(); }}
                                    className={`date-input-field date-input-field--range date-input-field--end${dateError ? ' date-input-field--error' : ''}`}
                                    disabled={loading}
                                    min={startDate}
                                />
                                <span className="date-inline-field__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                        <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                        <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                        {dateError && (
                            <span className="date-range-thread__error" role="alert">
                                {dateError}
                            </span>
                        )}
                    </div>
                )}

                {/* <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={yearOptions}
                        value={selectedYear}
                        onSelect={onYearChange}
                        placeholder={t('SELECT_YEAR') || 'Select Year'}
                        className="deduction-year-dropdown"
                    />
                </div>
                
                <div className="deduction-filters__dropdown">
                    <CustomDropdown
                        options={monthOptions}
                        value={selectedMonth}
                        onSelect={onMonthChange}
                        placeholder={t('SELECT_MONTH') || 'Select Month'}
                        className="deduction-month-dropdown"
                    />
                </div> */}


           
            </div>
        </div>
    );
};

export default DeductionFilters;
